<script setup lang="ts">
import { RouterLink } from 'vue-router';

import type { AppNavItem } from './nav-items';

defineProps<{
  items: AppNavItem[];
  /**
   * Whether labels are rendered. Geometry is deliberately identical in both
   * states — same paddings, same icon box — so the collapsed rail and the
   * expanded overlay put every icon on exactly the same x. That is what makes
   * the hover reveal read as "labels appear" instead of "the menu jumps".
   */
  expanded: boolean;
}>();

const emit = defineEmits<{
  navigate: [];
}>();
</script>

<template>
  <ul class="flex flex-col gap-0.5">
    <li
      v-for="item in items"
      :key="item.label"
    >
      <UTooltip
        :text="item.label"
        :disabled="expanded"
        :content="{ side: 'right' }"
      >
        <RouterLink
          :to="item.to"
          class="flex h-8 items-center gap-2 rounded-lg px-1 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          :class="item.active
            ? 'bg-elevated text-highlighted'
            : 'text-muted hover:bg-elevated/60 hover:text-default'"
          :aria-label="expanded ? undefined : item.label"
          :aria-current="item.active ? 'page' : undefined"
          :data-testid="item.testId"
          @click="emit('navigate')"
        >
          <span class="relative grid size-6 shrink-0 place-items-center">
            <UIcon
              :name="item.icon"
              class="size-[18px]"
            />

            <!-- Collapsed, the count has nowhere to go, so it becomes a dot on
                 the icon. Dropping it entirely would mean an agent only ever
                 learns they have notifications by hovering the rail — which is
                 the "watch the inbox with your eyes" problem the badge exists
                 to end, moved one level up. -->
            <span
              v-if="!expanded && item.badge"
              class="absolute -end-0.5 -top-0.5 size-2 rounded-full bg-primary ring-2 ring-muted"
              :data-testid="item.testId ? `${item.testId}-dot` : undefined"
            />
          </span>

          <!-- v-show, not v-if: keeping the node mounted lets the label fade
               with the panel instead of popping in after it has finished. -->
          <span
            v-show="expanded"
            class="truncate text-sm"
          >
            {{ item.label }}
          </span>

          <UBadge
            v-if="expanded && item.badge"
            :data-testid="item.testId ? `${item.testId}-badge` : undefined"
            color="neutral"
            variant="subtle"
            size="sm"
            class="ms-auto shrink-0 tabular-nums"
          >
            {{ item.badge }}
          </UBadge>
        </RouterLink>
      </UTooltip>
    </li>
  </ul>
</template>
